import React, { useState } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface MultiInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  helpText?: string;
  pattern?: string;
  patternErrorMessage?: string;
}

const MultiInput: React.FC<MultiInputProps> = ({
  values,
  onChange,
  label,
  placeholder = 'Ajouter une valeur...',
  disabled = false,
  className = '',
  inputClassName = '',
  helpText,
  pattern,
  patternErrorMessage = 'Valeur invalide'
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setError('');
  };

  const addValue = () => {
    if (!inputValue.trim()) return;
    
    // Validate the input if pattern is provided
    if (pattern && !new RegExp(pattern).test(inputValue)) {
      setError(patternErrorMessage);
      return;
    }

    // Prevent duplicates
    if (!values.includes(inputValue.trim())) {
      onChange([...values, inputValue.trim()]);
      setInputValue('');
      setError('');
    } else {
      setError('Cette valeur existe déjà');
    }
  };

  const removeValue = (valueToRemove: string) => {
    onChange(values.filter(v => v !== valueToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addValue();
    }
  };

  return (
    <div className={`${className}`}>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      
      <div className="relative mt-1 flex rounded-md shadow-sm">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          className={`block w-full rounded-l-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${inputClassName} ${error ? 'border-red-300' : ''}`}
        />
        <button
          type="button"
          onClick={addValue}
          disabled={disabled || !inputValue.trim()}
          className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
          <span className="ml-1">Ajouter</span>
        </button>
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      
      {helpText && (
        <p className="mt-1 text-sm text-gray-500">{helpText}</p>
      )}
      
      {values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {values.map((value, index) => (
            <div 
              key={index} 
              className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-sm font-medium text-blue-800"
            >
              <span>{value}</span>
              <button
                type="button"
                onClick={() => removeValue(value)}
                disabled={disabled}
                className="ml-1.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:outline-none focus:bg-blue-500 focus:text-white"
              >
                <XMarkIcon className="h-3 w-3" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiInput; 